'use client';

import { useCallback, useContext } from 'react';
import { AppSheetContext, type SheetIds } from '@frameio/shared/contexts';
import { APP_SHEET_ID } from '../components/AppSheet/AppSheet';

/**
 * A hook that will trigger an app sheet to open when clicked.
 * @example
 *
 * const bind = useAppSheetButton('uploads');
 *
 * <button {...bind}>Uploads</button>
 */

export function useAppSheetButton(id: SheetIds) {
  const { setId, id: currentSheetId } = useContext(AppSheetContext);
  const expanded = id === currentSheetId;

  const toggleSheet = useCallback(() => {
    setId((currentId) => {
      // close the sheet if it's already open
      if (id === currentId) {
        return null;
      }
      return id;
    });
  }, [setId, id]);

  return {
    props: {
      onKeyDown: useCallback(
        (e: React.KeyboardEvent) => {
          if (e.key === 'Enter') {
            toggleSheet();
          }
        },
        [toggleSheet]
      ),
      onPointerDown: useCallback(
        (e) => {
          if (e?.button === 2) return;
          if (e?.ctrlKey) return;
          toggleSheet();
        },
        [toggleSheet]
      ),
      'aria-controls': APP_SHEET_ID,
      'aria-expanded': expanded,
      'aria-haspopup': 'dialog' as const,
    },
    currentSheetId,
  };
}
